import React, { useCallback, useMemo } from 'react';

import { DollarCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { Empty, Form, Input, Modal as ModalAntD, Result, Tooltip } from 'antd';
import { useHistory } from 'react-router-dom';
import { T } from 'react-translator-component';

import * as S from './styled';
import {
  Profile,
  UseProfileListPagePros,
  useProfileListPage,
} from './useProfileListPage';

import { COLORS } from '../../../../utils/theme/colors';
import { Shell } from '../../../atoms/Shell/Shell';

const Modal: any = ModalAntD;

const ProfileListPage = () => {
  const history = useHistory();

  const {
    isNewOpen,
    setNewOpen,
    isProfileListLoading,
    profileList,
    isNewProfileLoading,
    onCreateProfile,
    errorNewProfile,
    goToListing,
    onTryToCreateAgain,
    deleteProfile,
  }: UseProfileListPagePros = useProfileListPage();

  const handleDelete = useCallback(
    (e: React.MouseEvent, uuid: string, name: string, editable?: boolean) => {
      e.stopPropagation();

      // Guarda: perfil sem permissão de edição é somente leitura
      if (!editable) return;

      ModalAntD.confirm({
        title: T('Confirm exclusion'),
        content: (
          <div>
            <p>
              {T('When deleting the Pricing Profile') +
                ` '${name}' ` +
                T(', it will be unlinked from all chargers.')}
            </p>
            <p>{T('Are you sure?')}</p>
          </div>
        ),

        okText: T('Yes'),
        cancelText: T('No'),
        okType: 'danger',
        centered: true,

        onOk: () =>
          deleteProfile(uuid, editable)
            .then((success) => {
              if (success) {
                ModalAntD.success({
                  content: T('Pricing Profile deleted successfully'),
                });
              } else {
                ModalAntD.error({
                  content: T('Error deleting the Pricing Profile'),
                });
              }
            })
            .catch(() => {
              ModalAntD.error({
                content: T('Error deleting the Pricing Profile'),
              });
            }),
      });
    },
    [deleteProfile],
  );

  const renderHeader = useMemo(
    () => (
      <S.Header>
        <S.Title>
          <DollarCircleOutlined color={COLORS.black} />

          {T('Pricing Profiles')}
        </S.Title>

        <S.NewButton
          size='large'
          type='primary'
          onClick={() => setNewOpen(true)}
        >
          {T('Create profile')}
        </S.NewButton>
      </S.Header>
    ),
    [setNewOpen],
  );

  const renderLoading = useMemo(
    () => (
      <S.BlankLoading>
        <Shell />
        <Shell />
        <Shell />
      </S.BlankLoading>
    ),
    [],
  );

  const renderEmpty = useMemo(
    () => (
      <S.Blank>
        <Empty
          description={T('No profiles found')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </S.Blank>
    ),
    [],
  );

  const renderProfileList = useMemo(
    () => (
      <S.Items>
        {profileList?.map(
          ({ chargeBoxCount, name, uuid, editable }: Profile, key: number) => (
            <S.Item
              key={`dynamic-pricing--profile-${name}-${key}`}
              onClick={() =>
                history.push({
                  pathname: `/dynamic-pricing/${uuid}`,
                  state: { profileId: uuid, profileName: name, editable },
                })
              }
            >
              {editable ? (
                <S.DeleteButton
                  onClick={(e) => handleDelete(e, uuid, name, editable)}
                >
                  <DeleteOutlined />
                </S.DeleteButton>
              ) : (
                <S.ReadOnlyTag>{T('Read only')}</S.ReadOnlyTag>
              )}

              <S.Icon>
                <DollarCircleOutlined />
              </S.Icon>

              <S.ItemInfo>
                <S.ItemTitle>
                  <Tooltip title={name}>{name}</Tooltip>
                </S.ItemTitle>

                <S.ItemSubTitle>
                  {chargeBoxCount
                    ? `${chargeBoxCount} ${T('applied chargers')}`
                    : `0 ${T('applied charger')}`}
                </S.ItemSubTitle>
              </S.ItemInfo>
            </S.Item>
          ),
        )}
      </S.Items>
    ),
    [history, profileList, handleDelete],
  );

  const handleProfileListRequest = useMemo(() => {
    if (isProfileListLoading) {
      return renderLoading;
    }

    if (!profileList?.length) {
      return renderEmpty;
    }

    return renderProfileList;
  }, [
    isProfileListLoading,
    profileList,
    renderProfileList,
    renderEmpty,
    renderLoading,
  ]);

  const [form] = Form.useForm();

  const renderNewProfile = useMemo(
    () => (
      <Modal
        visible={isNewOpen}
        title={T('Create profile')}
        onCancel={() => {
          setNewOpen(false);

          form.resetFields();
        }}
        footer={null}
        maskClosable={false}
        closable={!isNewProfileLoading}
      >
        <Form autoComplete='off' form={form} onFinish={onCreateProfile}>
          <S.Label>{T('Profile name')}</S.Label>

          <Form.Item
            name='name'
            rules={[{ required: true, message: T('Fill in the profile name') }]}
          >
            <Input size='large' />
          </Form.Item>

          <S.WrapperContinueButton>
            <S.Button
              block
              key='continue'
              htmlType='submit'
              size='large'
              type='primary'
              loading={isNewProfileLoading}
            >
              {T(isNewProfileLoading ? 'Creating the profile' : 'Continue')}
            </S.Button>
          </S.WrapperContinueButton>
        </Form>
      </Modal>
    ),
    [isNewOpen, setNewOpen, isNewProfileLoading, onCreateProfile, form],
  );

  const renderErrorNewProfile = useMemo(
    () => (
      <S.ErrorModal visible={errorNewProfile} closable={false} footer={null}>
        <Result
          status='error'
          title='Não foi possível criar o perfil!'
          extra={[
            <S.Button
              key='default'
              size='large'
              onClick={() => goToListing(form)}
            >
              Ver listagem
            </S.Button>,
            <S.Button
              type='primary'
              key='primary'
              size='large'
              onClick={onTryToCreateAgain}
            >
              Tentar novamente
            </S.Button>,
          ]}
        />
      </S.ErrorModal>
    ),
    [errorNewProfile, form, goToListing, onTryToCreateAgain],
  );

  return (
    <S.Div>
      {renderHeader}

      {handleProfileListRequest}

      {renderNewProfile}

      {renderErrorNewProfile}
    </S.Div>
  );
};

export default ProfileListPage;
